import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft, Sparkles, Upload, X, Download, RefreshCw, Image as ImageIcon, Trash2,
  Square, Smartphone, Monitor, RectangleVertical, Languages, ZoomIn, Minus, Plus, HelpCircle, Loader2,
  Search, ExternalLink, LayoutGrid, Wand2, Send, CheckSquare, ListChecks, Coins, SlidersHorizontal, MessageSquare, Paperclip,
  BarChart3, Film, Layers,
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../config';
import StripePaymentForm from './StripePaymentForm';
import MyConsumptionStatsModal from './MyConsumptionStatsModal';

// MVola repasse en declaration manuelle le 2026-08-27 (code automatique conserve cote backend
// mvola_routes.py/mvola_creditpay_bp - erreur MVola "Missing field"/formatError 4001 en attente
// de reponse de leur support, cf mail envoye).
const MVOLA_NUMBER = '034 26 126 52';
const MVOLA_ACCOUNT_NAME = 'TDI DIGITAL';
const ORANGE_NUMBER = '037 34 130 06';
const ORANGE_ACCOUNT_NAME = 'HERY NY AINA MIADANTSOA';
const DEFAULT_IMAGE_PRICE_AR = 350; // valeur de secours affichee le temps que /quota reponde - la vraie source est le serveur (grille tarifaire Appwrite, editable sans redeploiement)

function formatAr(n) { return `${(n || 0).toLocaleString('fr-FR')} Ar`; }

// Mode "Champs detailles" (alternative au prompt libre) : categories de base reprises de la
// bibliotheque de templates CM Studio (Environnement/Ambiance/Tenue/Mouvement/Action), etendues
// avec Personnage (visage/acteur) et Info complementaire, pour une coherence d'experience entre
// les modules TeckiA.
const STRUCTURED_FIELDS = [
  { key: 'environment', label: 'Environnement', placeholder: 'Ex : un salon moderne baigné de lumière naturelle...' },
  { key: 'ambiance', label: 'Ambiance', placeholder: 'Ex : chaleureuse, premium, lumière dorée du soir...' },
  { key: 'character', label: 'Personnage', placeholder: 'Ex : le visage de @image1, jeune femme malgache souriante...' },
  { key: 'outfit', label: 'Tenue', placeholder: 'Ex : la tenue de @image2...' },
  { key: 'movement', label: 'Mouvement', placeholder: 'Ex : légère rotation de la caméra...' },
  { key: 'action', label: 'Action', placeholder: 'Ex : tient le produit @image1 et sourit...' },
  { key: 'info', label: 'Info complémentaire', placeholder: 'Ex : tout autre détail important pour cette génération...' },
];

const ASPECT_RATIOS = [
  { value: '1:1', label: 'Carré', icon: Square },
  { value: '9:16', label: 'Story', icon: Smartphone },
  { value: '16:9', label: 'Paysage', icon: Monitor },
  { value: '3:4', label: 'Portrait', icon: RectangleVertical },
];

function TopupModal({ onClose, onSubmit, submitting, serviceId, onCardSuccess }) {
  // Etat local (pas remonte au parent) : voir le meme correctif dans IAStudioVideoPanel.jsx -
  // le parent re-rendait tout l'ecran a chaque frappe quand amount/method/ref_ vivaient la-bas
  // (latence signalee par Hery le 2026-08-20). onSubmit recoit desormais les valeurs directement.
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('mvola');
  const [ref_, setRef] = useState('');
  // Depuis la refonte tarifaire du 2026-08-24 : la carte n'accepte plus un montant libre, le
  // client choisit un pack (bonus credite en plus du prix paye). MVola et Orange restent en
  // montant libre, credites 1:1 sans bonus (declaration manuelle + validation admin).
  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [selectedPackId, setSelectedPackId] = useState(null);

  useEffect(() => {
    if (method !== 'card' || packs.length || loadingPacks) return;
    setLoadingPacks(true);
    axios.get(`${API_URL}/api/ia-studio/credit-packs`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
    }).then(res => {
      if (res.data.success) setPacks(res.data.packs);
    }).finally(() => setLoadingPacks(false));
  }, [method]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedPack = packs.find(p => p.pack_id === selectedPackId) || null;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-[#140a24] border border-purple-500/25 rounded-2xl w-full p-5 overflow-hidden transition-all ${method === 'card' && selectedPack ? 'max-w-md' : 'max-w-sm'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-base">Recharger mes crédits</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <label className="block text-white text-xs font-semibold mb-1.5">Méthode de paiement</label>
        <div className="flex gap-2 mb-3">
          {[{ id: 'mvola', label: 'MVola' }, { id: 'orange', label: 'Orange Money' }, { id: 'card', label: 'Carte bancaire' }].map(m => (
            <button
              key={m.id} onClick={() => setMethod(m.id)}
              className={`flex-1 py-2 rounded-xl text-xs font-bold border transition ${method === m.id ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {method === 'card' ? (
          <>
            <label className="block text-white text-xs font-semibold mb-1.5">Choisissez un pack</label>
            {loadingPacks ? (
              <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 text-purple-400 animate-spin" /></div>
            ) : (
              <div className="grid grid-cols-2 gap-2 mb-3">
                {packs.map(p => (
                  <button
                    key={p.pack_id}
                    onClick={() => setSelectedPackId(p.pack_id)}
                    className={`text-left p-2.5 rounded-xl border transition ${selectedPackId === p.pack_id ? 'bg-purple-600/30 border-purple-400' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                  >
                    <div className="text-white text-sm font-bold">{formatAr(p.price_ar)}</div>
                    <div className="text-purple-300 text-[11px] font-semibold">→ {formatAr(p.credited_ar)}</div>
                    {p.bonus_percent > 0 && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-amber-300">
                        <Sparkles className="w-3 h-3" /> +{p.bonus_percent}%
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
            {selectedPack ? (
              <StripePaymentForm
                key={selectedPack.pack_id}
                cart={[{ service: serviceId, pack_id: selectedPack.pack_id, quantity: 1 }]}
                getTotalAmount={() => selectedPack.price_ar}
                userId={localStorage.getItem('user_id')}
                onSuccess={onCardSuccess}
                onCancel={onClose}
              />
            ) : (
              !loadingPacks && (
                <p className="text-gray-500 text-[11px] mb-1">Sélectionnez un pack pour continuer.</p>
              )
            )}
          </>
        ) : (
          <>
            <label className="block text-white text-xs font-semibold mb-1.5">Montant (Ariary)</label>
            <input
              type="number" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="Ex : 20000"
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 mb-3"
            />
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3 text-[11px] text-purple-100 mb-3">
              Envoyez votre paiement au numéro <span className="font-semibold">{method === 'mvola' ? MVOLA_NUMBER : ORANGE_NUMBER}</span>{' '}
              au nom de <span className="font-semibold">{method === 'mvola' ? MVOLA_ACCOUNT_NAME : ORANGE_ACCOUNT_NAME}</span>, puis indiquez la référence reçue par SMS ci-dessous.
            </div>
            <label className="block text-white text-xs font-semibold mb-1.5">Référence de paiement</label>
            <input
              value={ref_} onChange={e => setRef(e.target.value)}
              placeholder="Numéro de transaction..."
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 mb-4"
            />
            <p className="text-gray-500 text-[11px] mb-4">
              Effectuez le paiement puis déclarez-le ici. Votre solde sera crédité après vérification.
            </p>
            <button
              onClick={() => onSubmit(amount, method, ref_)}
              disabled={!amount || !ref_.trim() || submitting}
              className="w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition"
              style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)' }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
              Déclarer le paiement
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function TeckiALoader({ subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center gap-5 py-12">
      <div className="relative w-36 h-36 flex items-center justify-center">
        <div className="ia-loader-radar absolute w-24 h-24 rounded-full border-2 border-purple-400" />
        <div className="ia-loader-ring absolute w-32 h-32">
          {[0, 90, 180, 270].map(deg => (
            <div
              key={deg}
              className="absolute w-2.5 h-2.5 rounded-full bg-purple-300"
              style={{
                boxShadow: '0 0 8px 2px rgba(139,92,246,0.8)',
                top: deg === 0 ? 0 : deg === 180 ? 'auto' : '50%',
                bottom: deg === 180 ? 0 : 'auto',
                left: deg === 270 ? 0 : deg === 90 ? 'auto' : '50%',
                right: deg === 90 ? 0 : 'auto',
                transform: (deg === 0 || deg === 180) ? 'translateX(-50%)' : (deg === 90 || deg === 270) ? 'translateY(-50%)' : undefined,
              }}
            />
          ))}
        </div>
        <div className="ia-loader-orb w-[76px] h-[76px] rounded-3xl overflow-hidden shadow-[0_0_24px_rgba(139,92,246,0.6)]">
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-300 via-purple-500 to-indigo-600">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
        </div>
      </div>
      <div className="text-center">
        <div className="text-white font-bold text-sm">TeckiA génère votre image...</div>
        <div className="text-gray-400 text-xs mt-1">{subtitle}</div>
      </div>
    </div>
  );
}

function Lightbox({ src, onClose }) {
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const onWheel = (e) => {
    e.preventDefault();
    setZoom(z => Math.min(4, Math.max(1, z - e.deltaY * 0.0015)));
  };

  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    dragging.current = true;
    last.current = { x: e.clientX, y: e.clientY };
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - last.current.x;
    const dy = e.clientY - last.current.y;
    last.current = { x: e.clientX, y: e.clientY };
    setPos(p => ({ x: p.x + dx, y: p.y + dy }));
  };
  const stopDrag = () => { dragging.current = false; };

  const toggleZoom = () => {
    if (zoom > 1) { setZoom(1); setPos({ x: 0, y: 0 }); } else { setZoom(2.2); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      onWheel={onWheel}
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center z-10"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-white/10 backdrop-blur px-4 py-2 rounded-full z-10" onClick={e => e.stopPropagation()}>
        <button onClick={() => setZoom(z => Math.max(1, z - 0.4))} className="text-white hover:text-purple-300"><Minus className="w-4 h-4" /></button>
        <span className="text-white text-xs w-10 text-center">{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(z => Math.min(4, z + 0.4))} className="text-white hover:text-purple-300"><Plus className="w-4 h-4" /></button>
        <a href={src} download target="_blank" rel="noopener noreferrer" className="text-white hover:text-purple-300 ml-2" onClick={e => e.stopPropagation()}>
          <Download className="w-4 h-4" />
        </a>
      </div>

      <img
        src={src}
        alt=""
        onClick={e => { e.stopPropagation(); toggleZoom(); }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
        draggable={false}
        style={{
          transform: `translate(${pos.x}px, ${pos.y}px) scale(${zoom})`,
          cursor: zoom > 1 ? 'grab' : 'zoom-in',
          transition: dragging.current ? 'none' : 'transform 0.15s ease-out',
        }}
        className="max-w-[90vw] max-h-[85vh] rounded-lg select-none"
      />
    </div>
  );
}

function LibraryDetail({ item, onClose, onUse }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#140a24] border border-purple-500/25 rounded-2xl max-w-3xl w-full max-h-[88vh] overflow-y-auto shadow-[0_0_60px_rgba(147,51,234,0.25)]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="text-white font-bold text-base pr-4">{item.title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center flex-shrink-0">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-5 p-5">
          <div>
            {item.thumbnail_url && (
              <img src={item.thumbnail_url} alt="" className="w-full rounded-xl border border-white/10" loading="lazy" />
            )}
            {item.author_name && (
              <a
                href={item.author_link} target="_blank" rel="noopener noreferrer"
                className="mt-2 flex items-center gap-1 text-[11px] text-gray-500 hover:text-purple-300 transition"
              >
                <ExternalLink className="w-3 h-3" /> via {item.author_name}
              </a>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3.5">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-purple-300 mb-1.5">
                <HelpCircle className="w-3.5 h-3.5" /> Explication
              </div>
              <p className="text-gray-200 text-xs leading-relaxed">{item.explanation_fr}</p>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-gray-400 mb-1.5">Prompt (français)</div>
              <p className="text-gray-300 text-xs leading-relaxed bg-black/30 border border-white/10 rounded-xl p-3.5 max-h-40 overflow-y-auto">
                {item.prompt_fr}
              </p>
            </div>
            <button
              onClick={() => onUse(item)}
              className="mt-auto flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white text-sm transition hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)' }}
            >
              <Wand2 className="w-4 h-4" /> Utiliser ce prompt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UseInVideoModal({ onClose, onChoose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#140a24] border border-purple-500/25 rounded-2xl max-w-sm w-full p-5"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-base">Utiliser dans TeckiA Video Studio</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <p className="text-gray-400 text-xs mb-4">Cette image sera ajoutée comme photo de référence. Choisissez le mode de génération vidéo.</p>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => onChoose('normal')}
            className="flex flex-col items-center gap-2 py-4 rounded-xl border border-white/10 bg-white/5 hover:border-purple-400/50 hover:bg-purple-600/10 transition text-white"
          >
            <Film className="w-6 h-6 text-purple-300" />
            <span className="text-xs font-bold">Vidéo normale</span>
          </button>
          <button
            onClick={() => onChoose('storyboard')}
            className="flex flex-col items-center gap-2 py-4 rounded-xl border border-white/10 bg-white/5 hover:border-purple-400/50 hover:bg-purple-600/10 transition text-white"
          >
            <Layers className="w-6 h-6 text-purple-300" />
            <span className="text-xs font-bold">Storyboard</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function IAStudioPanel({ onBack, onUseInVideoStudio }) {
  const token = localStorage.getItem('auth_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [providers, setProviders] = useState({});
  const [provider, setProvider] = useState('nanobanana');
  const [resolution, setResolution] = useState('');
  const [prompt, setPrompt] = useState('');
  const [promptMode, setPromptMode] = useState('free'); // 'free' | 'structured'
  const [fieldEnvironment, setFieldEnvironment] = useState('');
  const [fieldAmbiance, setFieldAmbiance] = useState('');
  const [fieldCharacter, setFieldCharacter] = useState('');
  const [fieldOutfit, setFieldOutfit] = useState('');
  const [fieldMovement, setFieldMovement] = useState('');
  const [fieldAction, setFieldAction] = useState('');
  const [fieldInfo, setFieldInfo] = useState('');
  const [structuredFocus, setStructuredFocus] = useState('action');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [refFiles, setRefFiles] = useState([]);
  const [refPreviews, setRefPreviews] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [credits, setCredits] = useState(0);
  const [imagePrice, setImagePrice] = useState(DEFAULT_IMAGE_PRICE_AR);
  const [showTopup, setShowTopup] = useState(false);
  const [showMyStats, setShowMyStats] = useState(false);
  const [submittingTopup, setSubmittingTopup] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [explanationLang, setExplanationLang] = useState('fr');
  const [explaining, setExplaining] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [refining, setRefining] = useState(false);
  const [activeTab, setActiveTab] = useState('create');
  const [library, setLibrary] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryItem, setLibraryItem] = useState(null);
  const [useInVideoTarget, setUseInVideoTarget] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deletingBatch, setDeletingBatch] = useState(false);
  const pollRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const fieldFileInputRef = useRef(null);
  const [pendingFieldKey, setPendingFieldKey] = useState(null);

  const structuredSetters = {
    environment: setFieldEnvironment, ambiance: setFieldAmbiance, character: setFieldCharacter, outfit: setFieldOutfit,
    movement: setFieldMovement, action: setFieldAction, info: setFieldInfo,
  };

  const structuredJoined = [fieldEnvironment, fieldAmbiance, fieldCharacter, fieldOutfit, fieldMovement, fieldAction, fieldInfo].filter(s => s.trim()).join(' ');
  const effectivePrompt = promptMode === 'structured' ? structuredJoined : prompt;
  const providerCfg = providers[provider];
  // Prix reel selon le provider/resolution choisi - remplace imagePrice (forfait /quota, garde
  // en repli tant que /image/providers n'a pas repondu) des que providerCfg est disponible.
  const currentPrice = providerCfg
    ? (providerCfg.flat_pricing ? (providerCfg.pricing?.flat ?? imagePrice) : (providerCfg.pricing?.[resolution] ?? imagePrice))
    : imagePrice;

  useEffect(() => {
    loadProviders();
    loadQuota();
    loadHistory();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'library' && library.length === 0) loadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadLibrary = async () => {
    setLoadingLibrary(true);
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/library`, { headers: authHeaders });
      if (res.data.success) setLibrary(res.data.items);
    } catch (e) { /* silencieux */ }
    setLoadingLibrary(false);
  };

  const useLibraryItem = (item) => {
    setPrompt(item.prompt_fr);
    setExplanation(item.explanation_fr);
    setExplanationLang('fr');
    setLibraryItem(null);
    setActiveTab('create');
  };

  const filteredLibrary = librarySearch.trim()
    ? library.filter(it =>
        it.title.toLowerCase().includes(librarySearch.toLowerCase())
        || it.description.toLowerCase().includes(librarySearch.toLowerCase()))
    : library;

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds([]);
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0 || deletingBatch) return;
    if (!window.confirm(`Supprimer définitivement ${selectedIds.length} prompt(s) de la bibliothèque ?`)) return;
    setDeletingBatch(true);
    try {
      await axios.post(`${API_URL}/api/ia-studio/library/delete-batch`, { ids: selectedIds }, { headers: authHeaders });
      setLibrary(prev => prev.filter(it => !selectedIds.includes(it.id)));
      exitSelectMode();
    } catch (e) { /* silencieux */ }
    setDeletingBatch(false);
  };

  const loadProviders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/image/providers`, { headers: authHeaders });
      if (res.data.success) {
        setProviders(res.data.providers);
        const cfg = res.data.providers['nanobanana'];
        if (cfg && !cfg.flat_pricing && cfg.resolutions?.length) setResolution(cfg.resolutions[0]);
      }
    } catch (e) { /* silencieux */ }
  };

  const selectProvider = (key) => {
    setProvider(key);
    const cfg = providers[key];
    if (cfg && !cfg.flat_pricing && cfg.resolutions?.length) setResolution(cfg.resolutions[0]);
  };

  const loadQuota = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/quota`, { headers: authHeaders });
      if (res.data.success) {
        setCredits(res.data.balance || 0);
        if (res.data.price_ar) setImagePrice(res.data.price_ar);
      }
    } catch (e) { /* silencieux */ }
  };

  // Paiement carte reussi cote Stripe (stripe.confirmPayment) - le credit reel se fait ensuite
  // cote serveur via le webhook /api/stripe/webhook (payment_intent.succeeded), donc on relance
  // loadQuota() a quelques secondes d'intervalle plutot qu'une seule fois pour laisser le temps
  // au webhook d'arriver.
  const handleCardTopupSuccess = () => {
    setShowTopup(false);
    window.alert('Paiement réussi ! Votre solde sera mis à jour dans quelques secondes.');
    loadQuota();
    setTimeout(loadQuota, 3000);
    setTimeout(loadQuota, 8000);
  };

  const submitTopup = async (amount, method, ref) => {
    if (!amount || !ref.trim() || submittingTopup) return;
    setSubmittingTopup(true);
    try {
      await axios.post(`${API_URL}/api/ia-studio/image/credits/order`, {
        amount_ar: parseInt(amount, 10), method, payment_ref: ref.trim(),
      }, { headers: authHeaders });
      setShowTopup(false);
      window.alert('Demande envoyée ! Votre solde sera crédité après vérification du paiement.');
    } catch (e) {
      window.alert(e?.response?.data?.error || 'Erreur');
    }
    setSubmittingTopup(false);
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/history`, { headers: authHeaders });
      if (res.data.success) setHistory(res.data.items);
    } catch (e) { /* silencieux */ }
    setLoadingHistory(false);
  };

  const addRefFiles = (fileList) => {
    const room = 7 - refFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    setRefFiles(prev => [...prev, ...arr]);
    arr.forEach(f => {
      const reader = new FileReader();
      reader.onload = e => setRefPreviews(prev => [...prev, e.target.result]);
      reader.readAsDataURL(f);
    });
  };

  const removeRef = (idx) => {
    setRefFiles(prev => prev.filter((_, i) => i !== idx));
    setRefPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const insertImageTag = (n) => {
    const tag = `@image${n}`;
    const setter = promptMode === 'structured' ? structuredSetters[structuredFocus] : setPrompt;
    setter(prev => {
      const trimmed = (prev || '').trimEnd();
      if (trimmed.includes(tag)) return prev;
      return trimmed ? `${trimmed} ${tag} ` : `${tag} `;
    });
  };

  // Attache une image directement a un champ detaille (ex: Environnement) : l'image rejoint
  // la meme banque de references partagee (plafond 7 au total) et son tag @imageN est inséré
  // automatiquement dans CE champ precis, sans passer par le focus/clic manuel sur la vignette.
  const attachImageToField = (key, fileList) => {
    const room = 7 - refFiles.length;
    if (room <= 0) {
      window.alert('Maximum 7 images de référence atteint (toutes catégories confondues).');
      return;
    }
    const arr = Array.from(fileList).slice(0, room);
    if (arr.length === 0) return;
    const tag = `@image${refFiles.length + 1}`;
    setRefFiles(prev => [...prev, ...arr]);
    arr.forEach(f => {
      const reader = new FileReader();
      reader.onload = e => setRefPreviews(prev => [...prev, e.target.result]);
      reader.readAsDataURL(f);
    });
    structuredSetters[key](prev => {
      const trimmed = (prev || '').trimEnd();
      if (trimmed.includes(tag)) return prev;
      return trimmed ? `${trimmed} ${tag} ` : `${tag} `;
    });
  };

  const openFieldImagePicker = (key) => {
    if (refFiles.length >= 7) {
      window.alert('Maximum 7 images de référence atteint (toutes catégories confondues).');
      return;
    }
    setPendingFieldKey(key);
    fieldFileInputRef.current?.click();
  };

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    pollRef.current = null;
    pollTimeoutRef.current = null;
  };

  const cancelGeneration = () => {
    stopPolling();
    setGenerating(false);
    setCurrentJob({ status: 'failed', error: 'Génération annulée.' });
  };

  const pollStatus = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/ia-studio/status/${jobId}`, { headers: authHeaders });
        const job = res.data;
        if (job.status === 'completed') {
          stopPolling();
          setCurrentJob({ status: 'completed', image_url: job.image_url, was_translated: job.was_translated, final_prompt: job.final_prompt });
          setGenerating(false);
          loadQuota();
          loadHistory();
        } else if (job.status === 'failed') {
          stopPolling();
          setCurrentJob({ status: 'failed', error: job.error || 'Échec de la génération' });
          setGenerating(false);
        }
      } catch (e) { /* on retente au prochain tick */ }
    }, 2500);
  };

  const generate = async () => {
    if (!effectivePrompt.trim() || generating || credits < currentPrice) return;
    stopPolling();
    setGenerating(true);
    setCurrentJob(null);
    try {
      const formData = new FormData();
      formData.append('provider', provider);
      if (providerCfg && !providerCfg.flat_pricing) formData.append('resolution', resolution);
      if (promptMode === 'structured') {
        formData.append('field_environment', fieldEnvironment.trim());
        formData.append('field_ambiance', fieldAmbiance.trim());
        formData.append('field_character', fieldCharacter.trim());
        formData.append('field_outfit', fieldOutfit.trim());
        formData.append('field_movement', fieldMovement.trim());
        formData.append('field_action', fieldAction.trim());
        formData.append('field_info', fieldInfo.trim());
      } else {
        formData.append('prompt', prompt.trim());
      }
      formData.append('aspect_ratio', aspectRatio);
      refFiles.forEach(f => formData.append('reference_images', f));
      const res = await axios.post(`${API_URL}/api/ia-studio/generate-image`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setCurrentJob({ status: 'queued' });
        pollStatus(res.data.job_id);
      } else {
        setGenerating(false);
      }
    } catch (e) {
      setGenerating(false);
      if (e?.response?.status === 402) {
        loadQuota();
        setCurrentJob({ status: 'failed', error: e.response.data?.message || 'Solde insuffisant' });
      } else {
        setCurrentJob({ status: 'failed', error: e?.response?.data?.error || 'Erreur de génération' });
      }
    }
  };

  const explainPrompt = async (lang) => {
    if (!prompt.trim()) return;
    setExplanationLang(lang);
    setExplaining(true);
    try {
      const formData = new FormData();
      formData.append('prompt', prompt.trim());
      formData.append('lang', lang);
      refFiles.forEach(f => formData.append('reference_images', f));
      const res = await axios.post(`${API_URL}/api/ia-studio/explain-prompt`, formData, { headers: authHeaders });
      if (res.data.success) setExplanation(res.data.explanation);
    } catch (e) { /* silencieux */ }
    setExplaining(false);
  };

  const refinePrompt = async () => {
    if (!feedback.trim() || refining) return;
    setRefining(true);
    try {
      const formData = new FormData();
      formData.append('prompt', prompt.trim());
      formData.append('feedback', feedback.trim());
      refFiles.forEach(f => formData.append('reference_images', f));
      const res = await axios.post(`${API_URL}/api/ia-studio/refine-prompt`, formData, { headers: authHeaders });
      if (res.data.success) {
        setPrompt(res.data.prompt);
        setExplanation(res.data.explanation);
        setExplanationLang('fr');
        setFeedback('');
      }
    } catch (e) { /* silencieux */ }
    setRefining(false);
  };

  const deleteItem = async (jobId) => {
    if (!window.confirm('Supprimer définitivement cette image ?')) return;
    try {
      await axios.delete(`${API_URL}/api/ia-studio/delete/${jobId}`, { headers: authHeaders });
      loadHistory();
    } catch (e) { /* silencieux */ }
  };

  return (
    <div className="min-h-screen bg-[#07050d] pb-16 relative">
      {/* Halos décoratifs — très discrets, ne doivent jamais couvrir le contenu */}
      <div className="pointer-events-none fixed -top-32 -left-32 w-72 h-72 bg-purple-700/10 rounded-full blur-3xl" />
      <div className="pointer-events-none fixed top-32 -right-32 w-72 h-72 bg-fuchsia-700/[0.07] rounded-full blur-3xl" />

      {/* Barre sticky : retour + quota + titre + onglets + recherche (si bibliothèque), toujours visible au scroll */}
      <div className="sticky top-0 z-40 bg-[#07050d]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 pt-4 pb-3">
          <div className="flex items-center justify-between mb-3">
            <button onClick={onBack} className="flex items-center gap-2 text-white/70 hover:text-white transition text-sm">
              <ArrowLeft className="w-4 h-4" /> Retour
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMyStats(true)}
                title="Mes statistiques"
                className="flex items-center justify-center w-8 h-8 rounded-full border bg-white/5 text-gray-300 border-white/10 hover:border-amber-400/50 hover:text-amber-300 transition"
              >
                <BarChart3 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setShowTopup(true)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition hover:scale-105 ${credits < currentPrice ? 'bg-red-500/15 text-red-300 border-red-500/30' : 'bg-white/5 text-gray-300 border-white/10'}`}
              >
                <Coins className="w-3.5 h-3.5" /> {credits.toLocaleString('fr-FR')} Ar
              </button>
            </div>
          </div>

          <h1 className="text-xl font-black tracking-tight mb-3 flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400 drop-shadow-[0_0_10px_rgba(168,85,247,0.6)]" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-200 via-fuchsia-200 to-purple-200">TeckiA Image Studio</span>
          </h1>

          <div className="flex gap-2 bg-white/5 border border-white/10 rounded-xl p-1">
            <button
              onClick={() => setActiveTab('create')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition ${activeTab === 'create' ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-[0_0_16px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <Wand2 className="w-4 h-4" /> Créer
            </button>
            <button
              onClick={() => setActiveTab('library')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition ${activeTab === 'library' ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-[0_0_16px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <LayoutGrid className="w-4 h-4" /> Bibliothèque de prompts
            </button>
          </div>

          {activeTab === 'library' && (
            <div className="flex items-center gap-2 mt-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-gray-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  value={librarySearch}
                  onChange={e => setLibrarySearch(e.target.value)}
                  placeholder="Rechercher un style, un thème..."
                  className="w-full bg-[#140a24]/95 border border-purple-500/15 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400"
                />
              </div>
              <button
                onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
                title="Sélectionner et supprimer"
                className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border transition ${selectMode ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:text-gray-200'}`}
              >
                <ListChecks className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-6 relative">
        {activeTab === 'library' && (
          <div className="mb-8">
            {loadingLibrary ? (
              <div className="text-center text-gray-400 text-sm py-12">Chargement de la bibliothèque...</div>
            ) : filteredLibrary.length === 0 ? (
              <div className="text-center text-gray-400 text-sm py-12">Aucun résultat.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredLibrary.map(item => {
                  const isSelected = selectedIds.includes(item.id);
                  return (
                  <button
                    key={item.id}
                    onClick={() => selectMode ? toggleSelect(item.id) : setLibraryItem(item)}
                    className={`group relative rounded-xl overflow-hidden border aspect-[3/4] text-left transition ${isSelected ? 'border-purple-400 ring-2 ring-purple-400/50' : 'border-white/10 hover:border-purple-400/50 hover:shadow-[0_0_20px_rgba(147,51,234,0.3)]'}`}
                  >
                    {item.thumbnail_url && (
                      <img src={item.thumbnail_url} alt="" loading="lazy" className="w-full h-full object-cover transition duration-300 group-hover:scale-110" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />
                    {selectMode && (
                      <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center border-2 ${isSelected ? 'bg-purple-600 border-purple-300' : 'bg-black/40 border-white/40'}`}>
                        {isSelected && <CheckSquare className="w-3.5 h-3.5 text-white" />}
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-2.5">
                      <div className="text-white text-xs font-semibold leading-tight">{item.title}</div>
                    </div>
                  </button>
                  );
                })}
              </div>
            )}

            {selectMode && selectedIds.length > 0 && (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-[#1a0f2e] border border-purple-500/30 rounded-2xl px-5 py-3 shadow-[0_0_40px_rgba(147,51,234,0.4)]">
                <span className="text-white text-sm font-semibold">{selectedIds.length} sélectionné(s)</span>
                <button
                  onClick={deleteSelected}
                  disabled={deletingBatch}
                  className="flex items-center gap-1.5 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-bold px-3.5 py-2 rounded-xl transition"
                >
                  {deletingBatch ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  Supprimer
                </button>
                <button onClick={exitSelectMode} className="text-gray-400 hover:text-white text-xs">
                  Annuler
                </button>
              </div>
            )}
          </div>
        )}

        {/* Formulaire */}
        <div className={`relative bg-[#140a24]/95 border border-purple-500/15 rounded-2xl p-5 mb-8 shadow-[0_0_40px_rgba(147,51,234,0.08)] ${activeTab !== 'create' ? 'hidden' : ''}`}>
          <div className="flex gap-2 mb-4 bg-black/20 border border-white/10 rounded-xl p-1">
            <button
              onClick={() => setPromptMode('free')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition ${promptMode === 'free' ? 'bg-purple-600 text-white shadow-[0_0_12px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <MessageSquare className="w-3.5 h-3.5" /> Prompt libre
            </button>
            <button
              onClick={() => setPromptMode('structured')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition ${promptMode === 'structured' ? 'bg-purple-600 text-white shadow-[0_0_12px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" /> Champs détaillés
            </button>
          </div>

          {promptMode === 'free' ? (
          <>
          <label className="flex items-center gap-1.5 text-white font-semibold mb-2 text-sm">
            Décrivez l'image que vous voulez
            <span className="flex items-center gap-1 text-[10px] font-normal text-purple-300/80 bg-purple-500/10 px-2 py-0.5 rounded-full ml-1">
              <Languages className="w-3 h-3" /> Malagasy, français ou anglais
            </span>
          </label>
          <textarea
            value={prompt}
            onChange={e => { setPrompt(e.target.value); setExplanation(null); }}
            rows={3}
            placeholder="Ex : Un flacon de parfum posé sur un rocher au coucher du soleil, style publicitaire haut de gamme..."
            className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 focus:shadow-[0_0_0_3px_rgba(147,51,234,0.15)] resize-y min-h-[76px] transition"
          />

          {prompt.trim() && (
            <div className="mt-2.5">
              {!explanation && !explaining && (
                <button
                  onClick={() => explainPrompt(explanationLang)}
                  className="flex items-center gap-1.5 text-xs text-purple-300/90 hover:text-purple-200 transition"
                >
                  <HelpCircle className="w-3.5 h-3.5" /> Qu'est-ce que ce prompt va générer ?
                </button>
              )}
              {explaining && (
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Explication en cours...
                </div>
              )}
              {explanation && !explaining && (
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl px-3.5 py-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold text-purple-300">
                      <HelpCircle className="w-3.5 h-3.5" /> Explication
                    </span>
                    <div className="flex gap-1">
                      {['fr', 'mg'].map(l => (
                        <button
                          key={l}
                          onClick={() => explainPrompt(l)}
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full transition ${explanationLang === l ? 'bg-purple-600 text-white' : 'bg-white/5 text-gray-400 hover:text-gray-200'}`}
                        >
                          {l.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-200 text-xs leading-relaxed">{explanation}</p>
                  <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-purple-500/15">
                    <input
                      value={feedback}
                      onChange={e => setFeedback(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') refinePrompt(); }}
                      placeholder="Pas d'accord ? Dites ce qu'il faut changer..."
                      disabled={refining}
                      className="flex-1 bg-black/25 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs placeholder-gray-500 focus:outline-none focus:border-purple-400 disabled:opacity-50"
                    />
                    <button
                      onClick={refinePrompt}
                      disabled={!feedback.trim() || refining}
                      className="w-7 h-7 rounded-lg bg-purple-600 hover:bg-purple-500 disabled:opacity-40 flex items-center justify-center flex-shrink-0 transition"
                    >
                      {refining ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <Send className="w-3.5 h-3.5 text-white" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          </>
          ) : (
          <div>
            <div className="flex items-center gap-1.5 text-white font-semibold mb-3 text-sm">
              <SlidersHorizontal className="w-3.5 h-3.5 text-purple-300" />
              Champs détaillés
              <span className="text-[10px] font-normal text-gray-500 ml-1">— remplissez ce qui compte, laissez le reste vide</span>
            </div>
            <div className="space-y-3">
              {STRUCTURED_FIELDS.map(f => {
                const value = { environment: fieldEnvironment, ambiance: fieldAmbiance, character: fieldCharacter, outfit: fieldOutfit, movement: fieldMovement, action: fieldAction, info: fieldInfo }[f.key];
                const setter = structuredSetters[f.key];
                return (
                  <div key={f.key}>
                    <label className="block text-gray-400 text-xs font-semibold mb-1">{f.label}</label>
                    <div className="flex items-center gap-1.5">
                      <input
                        value={value}
                        onChange={e => setter(e.target.value)}
                        onFocus={() => setStructuredFocus(f.key)}
                        placeholder={f.placeholder}
                        className="w-full bg-black/30 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 transition"
                      />
                      <button
                        type="button"
                        onClick={() => openFieldImagePicker(f.key)}
                        title={`Attacher une image à ${f.label}`}
                        disabled={refFiles.length >= 7}
                        className="flex-shrink-0 w-9 h-9 rounded-xl border border-white/10 bg-white/5 hover:bg-purple-600/30 hover:border-purple-400/50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-gray-400 hover:text-purple-300 transition"
                      >
                        <Paperclip className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fieldFileInputRef}
              className="hidden"
              onChange={e => {
                if (pendingFieldKey) attachImageToField(pendingFieldKey, e.target.files);
                e.target.value = '';
                setPendingFieldKey(null);
              }}
            />
          </div>
          )}

          <div className="mt-4">
            <label className="block text-white font-semibold mb-2 text-sm">Format</label>
            <div className="flex flex-wrap gap-2">
              {ASPECT_RATIOS.map(ar => {
                const Icon = ar.icon;
                const active = aspectRatio === ar.value;
                return (
                  <button
                    key={ar.value}
                    onClick={() => setAspectRatio(ar.value)}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold border transition ${
                      active
                        ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 border-purple-400 text-white shadow-[0_0_16px_rgba(147,51,234,0.5)]'
                        : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/25 hover:text-gray-200'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {ar.label} <span className="opacity-60">{ar.value}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-white font-semibold mb-2 text-sm">Photos de référence (optionnel, jusqu'à 7)</label>
            {refPreviews.length > 0 && (
              <p className="text-[11px] text-gray-500 mb-2">
                Touchez une photo pour l'insérer {promptMode === 'structured' ? (
                  <>dans le champ <span className="text-purple-300">{STRUCTURED_FIELDS.find(f => f.key === structuredFocus)?.label}</span></>
                ) : 'dans le prompt'} (ex: <span className="text-purple-300">@image1</span>)
              </p>
            )}
            <div className="flex flex-wrap gap-3">
              {refPreviews.map((src, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => insertImageTag(idx + 1)}
                  className="relative w-20 h-20 rounded-xl overflow-hidden border border-purple-500/25 hover:border-purple-400 transition group"
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-center py-0.5">
                    <span className="text-[9px] font-bold text-purple-300">@image{idx + 1}</span>
                  </div>
                  <div
                    role="button"
                    onClick={e => { e.stopPropagation(); removeRef(idx); }}
                    className="absolute top-1 right-1 w-5 h-5 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center"
                  >
                    <X className="w-3 h-3 text-white" />
                  </div>
                </button>
              ))}
              {refFiles.length < 7 && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                >
                  <Upload className="w-5 h-5 mb-1" />
                  <span className="text-[10px]">Ajouter</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={e => { addRefFiles(e.target.files); e.target.value = ''; }}
              />
            </div>
          </div>

          <div className="mt-5">
            <label className="block text-white font-semibold mb-2 text-sm">Moteur</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(providers).map(([key, cfg]) => {
                const active = provider === key;
                return (
                  <div key={key} className="relative group">
                    <button
                      onClick={() => selectProvider(key)}
                      className={`relative w-full px-3 py-2.5 rounded-xl text-xs font-bold border text-left transition-all ${
                        active
                          ? 'bg-gradient-to-br from-purple-600 to-fuchsia-600 border-purple-400 text-white shadow-[0_0_16px_rgba(147,51,234,0.4)]'
                          : 'bg-white/5 border-white/10 text-gray-300 hover:border-white/25'
                      }`}
                    >
                      <div className="truncate">{cfg.label}</div>
                      <div className="text-[10px] font-normal opacity-70 mt-0.5">
                        {cfg.flat_pricing ? formatAr(cfg.pricing?.flat) : `dès ${formatAr(Math.min(...Object.values(cfg.pricing || {})))}`}
                      </div>
                    </button>
                    {cfg.info && (
                      <div className="pointer-events-none absolute z-20 left-0 right-0 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150">
                        <div className="bg-[#1c0f30] border border-purple-500/30 rounded-xl p-3 text-[11px] text-gray-300 leading-relaxed shadow-2xl">
                          {cfg.info}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {providerCfg && !providerCfg.flat_pricing && (
              <div className="flex flex-wrap gap-2 mt-2.5">
                {providerCfg.resolutions.map(r => (
                  <button
                    key={r}
                    onClick={() => setResolution(r)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${resolution === r ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
                  >
                    {r} · {formatAr(providerCfg.pricing?.[r])}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={`mt-4 flex items-center justify-between rounded-xl px-4 py-3 border ${
            credits < currentPrice ? 'bg-red-500/10 border-red-500/30' : 'bg-black/20 border-white/10'
          }`}>
            <span className="text-gray-400 text-xs flex items-center gap-1.5">
              <Coins className="w-3.5 h-3.5" /> Coût de cette génération
            </span>
            <span className={`font-bold text-sm ${credits < currentPrice ? 'text-red-300' : 'text-amber-300'}`}>
              {currentPrice.toLocaleString('fr-FR')} Ar
            </span>
          </div>

          <button
            onClick={credits < currentPrice ? () => setShowTopup(true) : generate}
            disabled={!effectivePrompt.trim() || generating}
            className="group w-full mt-3 py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 hover:scale-[1.015] active:scale-[0.99] shadow-[0_8px_24px_rgba(147,51,234,0.35)]"
            style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777, #9333EA)', backgroundSize: '200% 200%' }}
          >
            {generating ? (
              <>Génération en cours...</>
            ) : credits < currentPrice ? (
              <>Solde insuffisant — Recharger</>
            ) : (
              <><Sparkles className="w-5 h-5 group-hover:rotate-12 transition-transform" /> Générer l'image</>
            )}
          </button>
        </div>

        {/* Résultat courant */}
        {currentJob && (
          <div className="relative bg-[#140a24]/95 border border-purple-500/15 rounded-2xl p-5 mb-8 text-center shadow-[0_0_40px_rgba(147,51,234,0.08)]">
            {currentJob.status === 'queued' && (
              <>
                <TeckiALoader subtitle="Quelques secondes suffisent en général" />
                <button
                  onClick={cancelGeneration}
                  className="text-xs text-gray-400 hover:text-red-300 transition -mt-4 mb-2"
                >
                  Annuler
                </button>
              </>
            )}
            {currentJob.status === 'failed' && (
              <div className="py-6 text-red-300 text-sm">{currentJob.error}</div>
            )}
            {currentJob.status === 'completed' && (
              <>
                {currentJob.was_translated && (
                  <div className="flex items-center justify-center gap-1.5 text-[11px] text-purple-300/80 mb-3">
                    <Languages className="w-3 h-3" /> Prompt traduit et amélioré automatiquement
                  </div>
                )}
                <div className="relative inline-block group cursor-zoom-in" onClick={() => setLightboxSrc(currentJob.image_url)}>
                  <img src={currentJob.image_url} alt="" className="max-w-full max-h-[420px] mx-auto rounded-xl mb-4 transition group-hover:brightness-90" />
                  <div className="absolute inset-0 mb-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                    <div className="w-11 h-11 rounded-full bg-black/50 backdrop-blur flex items-center justify-center">
                      <ZoomIn className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <a
                    href={currentJob.image_url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition hover:scale-105"
                  >
                    <Download className="w-4 h-4" /> Télécharger
                  </a>
                  {onUseInVideoStudio && (
                    <button
                      onClick={() => setUseInVideoTarget(currentJob.image_url)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition hover:scale-105"
                    >
                      <Film className="w-4 h-4" /> Utiliser en vidéo
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Historique */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-white font-semibold text-sm">Vos dernières générations</h2>
          <button onClick={loadHistory} className="text-gray-400 hover:text-white transition hover:rotate-180 duration-500">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {loadingHistory ? (
          <div className="text-center text-gray-400 text-sm py-8">Chargement...</div>
        ) : history.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-8 flex flex-col items-center gap-2">
            <ImageIcon className="w-8 h-8 text-gray-700" />
            Aucune image générée pour l'instant.
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {history.map(item => (
              <div
                key={item.job_id}
                className="relative group rounded-xl overflow-hidden border border-white/10 aspect-square cursor-zoom-in transition hover:border-purple-400/50 hover:shadow-[0_0_20px_rgba(147,51,234,0.3)]"
                onClick={() => setLightboxSrc(item.image_url)}
              >
                <img src={item.image_url} alt="" className="w-full h-full object-cover transition duration-300 group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                  <a
                    href={item.image_url} download target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
                  >
                    <Download className="w-4 h-4 text-white" />
                  </a>
                  {onUseInVideoStudio && (
                    <button
                      onClick={e => { e.stopPropagation(); setUseInVideoTarget(item.image_url); }}
                      title="Utiliser dans TeckiA Video Studio"
                      className="w-8 h-8 rounded-full bg-white/20 hover:bg-purple-500/60 flex items-center justify-center"
                    >
                      <Film className="w-4 h-4 text-white" />
                    </button>
                  )}
                  <button
                    onClick={e => { e.stopPropagation(); deleteItem(item.job_id); }}
                    className="w-8 h-8 rounded-full bg-white/20 hover:bg-red-500/60 flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
      {libraryItem && <LibraryDetail item={libraryItem} onClose={() => setLibraryItem(null)} onUse={useLibraryItem} />}
      {useInVideoTarget && (
        <UseInVideoModal
          onClose={() => setUseInVideoTarget(null)}
          onChoose={(mode) => { onUseInVideoStudio(useInVideoTarget, mode); setUseInVideoTarget(null); }}
        />
      )}
      {showMyStats && <MyConsumptionStatsModal onClose={() => setShowMyStats(false)} />}
      {showTopup && (
        <TopupModal
          onClose={() => setShowTopup(false)} onSubmit={submitTopup}
          submitting={submittingTopup}
          serviceId="ia_studio_image"
          onCardSuccess={handleCardTopupSuccess}
        />
      )}
    </div>
  );
}
